import React, { useState, useEffect } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  Server,
  Gauge,
  Thermometer,
  Wind,
  Home,
  Bell,
  LogOut,
  Menu,
  Lightbulb,
  User,
  ChevronDown,
  Key,
  Bot,
  Settings,
  Building2,
  LayoutDashboard,
  SlidersHorizontal,
  Workflow,
  FileText,
  BarChart3
} from 'lucide-react';
import { useAppMode } from '../contexts/AppModeContext';
import { useTranslation } from '../contexts/LanguageContext';
import { mcpOAuthService } from '../services/mcpOAuthService';
import { authService } from '../services/authService';
import { ultronTuringApi } from '../services/ultronTuringApi';
import { logger } from '../utils/logger';

interface AppSidebarProps {
  onExpandedChange?: (expanded: boolean) => void;
  onNavigate?: (view: string) => void;
  onSettingsClick?: () => void;
  onTuringAIClick?: () => void;    // For Turing AI (unified AI management)
  activeView?: string;
}

export default function AppSidebar({
  onExpandedChange,
  onNavigate,
  onSettingsClick,
  onTuringAIClick,
  activeView
}: AppSidebarProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { mode, setMode } = useAppMode();
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState<string>(activeView || 'dashboard');
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('ULTRON');
  const [showBoardSelector, setShowBoardSelector] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userInfo, setUserInfo] = useState<{ email?: string; name?: string; photo?: string } | null>(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [appId, setAppId] = useState('KLMGeDMy');

  // Sync activeItem with activeView prop
  useEffect(() => {
    if (activeView) {
      setActiveItem(activeView);
    }
  }, [activeView]);

  // Load logo, company name and user info
  useEffect(() => {
    const savedLogo = localStorage.getItem('ultron-company-logo');
    const savedName = localStorage.getItem('ultron-company-name');
    if (savedLogo) setLogoUrl(savedLogo);
    if (savedName) setCompanyName(savedName);

    // Get user info based on auth mode
    const authMode = sessionStorage.getItem('auth_mode');
    if (authMode === 'oauth') {
      // OAuth user - fetch from GetUserInfo API (if available)
      const fetchUserInfo = async () => {
        try {
          // Check if we have a valid token first
          const tokens = sessionStorage.getItem('ultron_tokens') || localStorage.getItem('ultron_tokens');
          if (!tokens) {
            logger.log('⚠️ Sidebar: No tokens available, skipping GetUserInfo');
            return;
          }

          logger.log('🔍 Sidebar: Fetching user info...');
          const response = await ultronTuringApi.getUserInfo();
          logger.log('🔍 Sidebar: GetUserInfo response:', response);
          
          // Handle case where endpoint is not available (404)
          if (response?.available === false) {
            logger.log('⚠️ Sidebar: GetUserInfo endpoint not available, using fallback');
            // Try to get user info from token or use default
            try {
              const tokenData = JSON.parse(tokens);
              if (tokenData.email) {
                setUserInfo({ email: tokenData.email });
              }
            } catch {
              // Ignore parse errors
            }
            return;
          }
          
          if (response?.userInfo) {
            const newUserInfo = {
              name: response.userInfo.name,
              email: response.userInfo.email,
              photo: response.userInfo.photoURL
            };
            logger.log('🔍 Sidebar: Setting userInfo to:', newUserInfo);
            setUserInfo(newUserInfo);
          }
        } catch (error) {
          logger.warn('⚠️ Sidebar: Failed to fetch user info (non-critical):', error);
          // Don't show error to user - this is optional functionality
        }
      };
      fetchUserInfo();
    } else {
      // API Key mode user
      const credentials = localStorage.getItem('ultron-energy-credentials');
      if (credentials) {
        const parsed = JSON.parse(credentials);
        setUserInfo({ email: parsed.appId || 'API User' });
        setApiKey(parsed.apiKey || '');
        setAppId(parsed.appId || 'KLMGeDMy');
      }
    }
  }, []);

  // Notify parent when expanded state changes
  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);

  const handleBoardChange = (newMode: 'intelliSpace') => {
    // Legacy boards removed - only intelliSpace remains
    setMode(newMode);
    setActiveItem('dashboard');
    setShowBoardSelector(false);
  };

  const handleNavigate = (item: string) => {
    // ✅ Always update activeItem first for visual feedback
    setActiveItem(item);

    // Handle Turing AI direct click
    if (item === 'turing-ai' && onTuringAIClick) {
      onTuringAIClick();
      return;
    }

    // Handle regular navigation items
    if (item === 'settings' && onSettingsClick) {
      onSettingsClick();
    } else if (onNavigate) {
      onNavigate(item);
    }
  };

  const handleLogout = () => {
    // Clear all auth data
    mcpOAuthService.logout();
    authService.logout();
    localStorage.removeItem('ultron-energy-credentials');
    sessionStorage.clear();
    // Redirect to login
    window.location.href = '/login';
  };

  const handleSaveApiKey = () => {
    const credentials = localStorage.getItem('ultron-energy-credentials');
    const parsed = credentials ? JSON.parse(credentials) : {};
    const updated = {
      ...parsed,
      apiKey,
      appId,
      baseUrl: 'https://turing.ultronsmart.com'
    };
    localStorage.setItem('ultron-energy-credentials', JSON.stringify(updated));
    setShowApiKeyModal(false);
    // Reload to apply new credentials
    window.location.reload();
  };

  // Simplified: only intelliSpace board remains (legacy boards removed)
  const getBoardIcon = (_board: string) => {
    return <Building2 className="w-5 h-5 text-indigo-500" />;
  };

  const getBoardName = (_board: string) => {
    return t.sidebar.intelliSpace || 'IntelliSpace';
  };

  // Define menu items based on mode
  const getMenuItems = () => {
    if (mode === 'intelliSpace') {
      // IntelliSpace specific menu items (device-manager moved to bottom section)
      return [
        { id: 'overview', label: t.intelliSpace?.overview || 'Overview', icon: <LayoutDashboard className="w-5 h-5" /> },
        { id: 'control', label: t.intelliSpace?.control || 'Control', icon: <SlidersHorizontal className="w-5 h-5" /> },
        { id: 'alerts', label: t.sidebar?.alerts || 'Alerts', icon: <Bell className="w-5 h-5" /> },
      ];
    } else {
      // Default menu items for other modes
      const items = [
        { id: 'overview-dashboard', label: t.sidebar.dashboard, icon: <Home className="w-5 h-5" /> },
        { id: 'power-meter-view', label: t.sidebar.powerMeterView, icon: <Gauge className="w-5 h-5" /> },
        { id: 'devices', label: t.sidebar.devices, icon: <Server className="w-5 h-5" /> },
        { id: 'alerts', label: t.sidebar.alerts, icon: <Bell className="w-5 h-5" /> },
      ];
      // Conditionally add Turing AI menu item if onTuringAIClick is provided
      if (onTuringAIClick) {
        items.push({
          id: 'turing-ai',
          label: 'Turing AI',
          icon: <Bot className="w-5 h-5" />
        });
      }
      return items;
    }
  };

  const menuItems = getMenuItems();

  const bottomMenuItems = [
    { id: 'generate-report', label: t.sidebar?.generateReport || '報表產生', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'device-logs', label: t.sidebar?.deviceLogs || 'Device Logs', icon: <FileText className="w-5 h-5" /> },
    { id: 'divider-1', label: '', icon: null },
    { id: 'device-manager', label: t.intelliSpace?.deviceManager || 'Device Manager', icon: <Server className="w-5 h-5" /> },
    { id: 'divider-2', label: '', icon: null },
    { id: 'settings', label: t.sidebar.settings, icon: <Settings className="w-5 h-5" /> },
  ];

  return (
    <div className={`relative h-screen bg-gradient-to-b from-[#0f2d4a] via-[#0C2340] to-[#081a30] text-white transition-all duration-300 flex-shrink-0 ${
      isExpanded ? 'w-64' : 'w-16'
    } flex flex-col`}>
      {/* Header with Logo */}
      <div className="h-[65px] flex items-center justify-between border-b border-slate-700/50">
        {isExpanded ? (
          <div className="w-full flex items-center justify-between px-4">
            <div className="flex items-center space-x-3">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={companyName}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <div className="h-8 w-8 bg-blue-600 rounded flex items-center justify-center text-white font-bold">
                  U
                </div>
              )}
              <span className="font-semibold text-lg">{companyName}</span>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="p-1 hover:bg-[#1a3a5c] rounded-lg transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
          </div>
        ) : (
          <div className="w-full flex items-center justify-center">
            <button
              onClick={() => setIsExpanded(true)}
              className="p-2 hover:bg-[#1a3a5c] rounded-lg transition-colors"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={companyName}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <Menu size={24} />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Board Selector */}
      <div className={`border-b border-slate-700/50 h-14 flex items-center ${isExpanded ? 'px-3' : ''}`}>
        {isExpanded ? (
          <div className="w-full">
            <div className="relative">
              <button
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#1a3a5c] rounded-lg transition-colors"
                onClick={() => setShowBoardSelector(!showBoardSelector)}
              >
                <div className="flex items-center space-x-2">
                  {getBoardIcon(mode)}
                  <span className="text-sm font-medium">{getBoardName(mode)}</span>
                </div>
                <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${showBoardSelector ? 'rotate-90' : ''}`} />
              </button>

              {/* Board Dropdown Menu */}
              {showBoardSelector && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#0a1e38] rounded-lg shadow-xl border border-slate-700/50 overflow-hidden z-50">
                  <button
                    onClick={() => handleBoardChange('intelliSpace')}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 hover:bg-[#1a3a5c] transition-colors ${
                      mode === 'intelliSpace' ? 'bg-indigo-600/20 text-indigo-400' : 'text-slate-300'
                    }`}
                  >
                    <Building2 className="w-5 h-5 text-indigo-500" />
                    <span className="text-sm">{t.sidebar.intelliSpaceBoard || 'IntelliSpace 智能空間'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center w-full">
            <div className="relative">
              <button
                onClick={() => setShowBoardSelector(!showBoardSelector)}
                className={`p-2 rounded-lg transition-colors ${
                  showBoardSelector ? 'bg-[#1a3a5c]' : 'hover:bg-[#1a3a5c]'
                }`}
                title={getBoardName(mode)}
              >
                {getBoardIcon(mode)}
              </button>

              {/* Board Dropdown Menu (Collapsed) */}
              {showBoardSelector && (
                <div className="absolute left-full ml-2 top-0 bg-[#0a1e38] rounded-lg shadow-xl border border-slate-700/50 overflow-hidden z-50">
                  <button
                    onClick={() => handleBoardChange('intelliSpace')}
                    className={`flex items-center space-x-2 px-3 py-2 hover:bg-[#1a3a5c] transition-colors whitespace-nowrap ${
                      mode === 'intelliSpace' ? 'bg-indigo-600/20 text-indigo-400' : 'text-slate-300'
                    }`}
                  >
                    <Building2 className="w-4 h-4 text-indigo-500" />
                    <span className="text-xs">{t.sidebar.intelliSpace || 'IntelliSpace 智能空間'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Main Navigation */}
      <div className="flex-1 overflow-y-auto">
        {isExpanded ? (
          <nav className="p-3 space-y-1">
            {menuItems.map(item => (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                  activeItem === item.id
                    ? 'bg-blue-600 text-white'
                    : 'hover:bg-[#1a3a5c] text-slate-300'
                }`}
              >
                {item.icon}
                <span className="text-base font-medium flex-1 text-left">{item.label}</span>
              </button>
            ))}
          </nav>
        ) : (
          <div className="flex flex-col items-center py-3 space-y-2">
            {menuItems.map(item => (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={`p-2 rounded-lg transition-colors ${
                  activeItem === item.id
                    ? 'bg-blue-600 text-white'
                    : 'hover:bg-[#1a3a5c] text-slate-400'
                }`}
                title={item.label}
              >
                {item.icon}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Section */}
      <div className="border-t border-slate-700/50">
        {isExpanded ? (
          <div className="p-3 space-y-1">
            {bottomMenuItems.map(item => (
              item.id.startsWith('divider') ? (
                <div key={item.id} className="px-3 my-2">
                  <div className="border-t border-slate-700/50" />
                </div>
              ) : (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(item.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                    activeItem === item.id
                      ? 'bg-blue-600 text-white'
                      : 'hover:bg-[#1a3a5c] text-slate-300'
                  }`}
                >
                  {item.icon}
                  <span className="text-base font-medium">{item.label}</span>
                </button>
              )
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center py-3 space-y-2">
            {bottomMenuItems.filter(item => !item.id.startsWith('divider')).map(item => (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={`p-2 rounded-lg transition-colors ${
                  activeItem === item.id
                    ? 'bg-blue-600 text-white'
                    : 'hover:bg-[#1a3a5c] text-slate-400'
                }`}
                title={item.label}
              >
                {item.icon}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* User Account Section */}
      <div className="border-t border-slate-700/50">
        {isExpanded ? (
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="w-full p-3 hover:bg-[#1a3a5c] transition-colors"
            >
              <div className="flex items-center space-x-3 px-3">
                {userInfo?.photo ? (
                  <img
                    src={userInfo.photo}
                    alt="User Avatar"
                    className="w-8 h-8 rounded-full object-cover border border-slate-700/50"
                  />
                ) : (
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-sm text-white">
                    <User className="w-4 h-4" />
                  </div>
                )}
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-slate-200">
                    {userInfo?.name || userInfo?.email || 'User'}
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${
                  showUserMenu ? 'rotate-180' : ''
                }`} />
              </div>
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute bottom-full left-0 w-full bg-[#0a1e38] border border-slate-700/50 rounded-t-lg shadow-lg mb-1">
                {/* User Info - Only for OAuth users */}
                {sessionStorage.getItem('auth_mode') === 'oauth' && userInfo && (
                  <div className="px-6 py-4 border-b border-slate-700/50">
                    <div className="flex items-center space-x-3">
                      {userInfo.photo ? (
                        <img
                          src={userInfo.photo}
                          alt="User Avatar"
                          className="w-12 h-12 rounded-full object-cover border-2 border-slate-700/50"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">
                          {userInfo.name || 'ULTRON User'}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {userInfo.email}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {sessionStorage.getItem('auth_mode') !== 'oauth' && (
                  <button
                    onClick={() => {
                      setShowApiKeyModal(true);
                      setShowUserMenu(false);
                    }}
                    className="w-full flex items-center space-x-3 px-6 py-3 hover:bg-[#1a3a5c] transition-colors text-slate-300 hover:text-white border-b border-slate-700/50"
                  >
                    <Key className="w-4 h-4" />
                    <span className="text-sm">{t.sidebar.apiKeySettings}</span>
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-6 py-3 hover:bg-[#1a3a5c] transition-colors text-slate-300 hover:text-white"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">Logout</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="py-3 flex flex-col items-center">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="p-2 rounded-lg hover:bg-[#1a3a5c] transition-colors relative"
              title="Account"
            >
              <User className="w-5 h-5 text-slate-400" />
              {showUserMenu && (
                <div className="absolute bottom-full left-0 bg-[#0a1e38] border border-slate-700/50 rounded-lg shadow-lg mb-2 p-2 whitespace-nowrap">
                  {sessionStorage.getItem('auth_mode') !== 'oauth' && (
                    <button
                      onClick={() => {
                        setShowApiKeyModal(true);
                        setShowUserMenu(false);
                      }}
                      className="flex items-center space-x-2 px-3 py-2 hover:bg-[#1a3a5c] rounded transition-colors text-slate-300 hover:text-white"
                    >
                      <Key className="w-4 h-4" />
                      <span className="text-sm">{t.sidebar.apiKeySettings}</span>
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 px-3 py-2 hover:bg-[#1a3a5c] rounded transition-colors text-slate-300 hover:text-white mt-1"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="text-sm">Logout</span>
                  </button>
                </div>
              )}
            </button>
          </div>
        )}
      </div>

      {/* API Key Settings Modal */}
      {showApiKeyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-[#0a1e38] rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center space-x-3 mb-4">
              <Key className="w-6 h-6 text-blue-400" />
              <h2 className="text-xl font-semibold text-white">{t.sidebar.apiKeySettings}</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="apiKey" className="block text-sm font-medium text-slate-300 mb-2">
                  API Key
                </label>
                <input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a3a5c] border border-slate-700/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your API key"
                />
              </div>

              <div>
                <label htmlFor="appId" className="block text-sm font-medium text-slate-300 mb-2">
                  App ID
                </label>
                <select
                  id="appId"
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a3a5c] border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="KLMGeDMy">Ultron Turing Personal</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowApiKeyModal(false)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveApiKey}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}